import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { setupAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { countries, Country, getCountryByCode, allCurrencies } from '../../data/countries';

const RegisterOrganisationPage: React.FC = () => {
  useDocumentTitle('Register Organisation');
  const { setOrganizations, organizations, setCurrentOrganization } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    industry: '',
    size: '',
    country: '',
    currency: '',
  });
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const industryOptions = [
    'Technology',
    'E-commerce',
    'Finance',
    'Healthcare',
    'Education',
    'Media & Entertainment',
    'Travel & Hospitality',
    'Non-profit',
    'Government',
    'Other'
  ];

  const sizeOptions = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-1000 employees',
    '1000+ employees'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    const country = getCountryByCode(countryCode);

    if (country) {
      setSelectedCountry(country);
      setFormData(prev => ({
        ...prev,
        country: countryCode,
        currency: country.currency,
      }));
    } else {
      setSelectedCountry(null);
      setFormData(prev => ({
        ...prev,
        country: '',
        currency: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter your organization name');
      return;
    }

    if (!formData.industry) {
      toast.error('Please select your industry');
      return;
    }

    if (!formData.size) {
      toast.error('Please select your organization size');
      return;
    }

    if (!formData.country) {
      toast.error('Please select your country');
      return;
    }

    if (!formData.currency) {
      toast.error('Please select your currency');
      return;
    }

    setIsLoading(true);
    try {
      const response = await setupAPI.createOrganization(formData);
      if (response.data.status === 'success') {
        const newOrg = {
          id: response.data.organization_id || response.data.organization?.id,
          name: formData.name,
          role: 'owner',
          is_default: false,
        };

        // Update organizations list and set as current
        const updatedOrgs = [...organizations, newOrg];
        setOrganizations(updatedOrgs);
        setCurrentOrganization(newOrg);

        toast.success('Organization created successfully!');
        navigate('/organisation');
      } else {
        toast.error(response.data.message || 'Failed to create organization');
      }
    } catch (error: any) {
      console.error('Organization creation error:', error);
      toast.error(error.response?.data?.message || 'Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-xl-6">
            {/* Header */}
            <div className="mb-4">
              <button
                className="btn btn-link text-decoration-none p-0 mb-3"
                onClick={() => navigate(-1)}
              >
                <i className="bi bi-arrow-left me-2"></i>Back
              </button>
              <h2 className="h3 fw-bold text-dark mb-2">Register New Organization</h2>
              <p className="text-muted">Create a new organization to manage separate clients or businesses</p>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Organization Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter organization name"
                    />
                    <div className="form-text">This could be your client's company name</div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="website" className="form-label">
                      Website URL (Optional)
                    </label>
                    <input
                      id="website"
                      name="website"
                      type="url"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="industry" className="form-label">
                        Industry <span className="text-danger">*</span>
                      </label>
                      <select
                        id="industry"
                        name="industry"
                        required
                        value={formData.industry}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select industry</option>
                        {industryOptions.map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="size" className="form-label">
                        Organization Size <span className="text-danger">*</span>
                      </label>
                      <select
                        id="size"
                        name="size"
                        required
                        value={formData.size}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select size</option>
                        {sizeOptions.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="country" className="form-label">
                        Country <span className="text-danger">*</span>
                      </label>
                      <select
                        id="country"
                        name="country"
                        required
                        value={formData.country}
                        onChange={handleCountryChange}
                        className="form-select"
                      >
                        <option value="">Select country</option>
                        {countries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="currency" className="form-label">
                        Currency <span className="text-danger">*</span>
                      </label>
                      <select
                        id="currency"
                        name="currency"
                        required
                        value={formData.currency}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        <option value="">Select currency</option>
                        {allCurrencies.map((curr) => (
                          <option key={curr.code} value={curr.code}>
                            {curr.code} - {curr.name}
                          </option>
                        ))}
                      </select>
                      {selectedCountry && formData.currency === selectedCountry.currency && (
                        <div className="form-text text-success">
                          <i className="bi bi-check-circle me-1"></i>
                          Auto-selected based on country
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="btn btn-outline-secondary flex-grow-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="btn btn-primary flex-grow-1"
                    >
                      {isLoading ? <LoadingSpinner size="sm" /> : 'Create Organization'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Info Card */}
            <div className="card border-0 shadow-sm mt-4">
              <div className="card-body">
                <div className="d-flex align-items-start gap-3">
                  <div className="bg-primary bg-opacity-10 rounded p-2">
                    <i className="bi bi-info-circle text-primary fs-5"></i>
                  </div>
                  <div>
                    <h6 className="mb-1">Managing Multiple Organizations</h6>
                    <p className="text-muted small mb-0">
                      You can register multiple organizations to manage different clients or businesses.
                      Each organization has its own assets, team members, and settings.
                      Use the organization switcher in the header to quickly switch between them.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RegisterOrganisationPage;
